import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { useBottomsheet } from '@/devtools/screens_deprecated/useBottomsheet.hook';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { CurrenciesIcon } from '@/features/earn/components';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { FundSourceSelectFilters, RemittancePurposeSelectFilters } from '@/features/bill-payment/screens/union-pay/options';
import { UnionType } from '@/features/bill-payment/enum';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
export interface PlacedNativeScreenProps {
  onBack?: (flag?: boolean) => void;
  wallet?: any;
  calcFee?: any;
  beneficiaryInformation?: any;
  refId?: string;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const PlacedNativeScreen: React.FC<PlacedNativeScreenProps> = (props) => {
  const {
    onBack,
    wallet,
    calcFee,
    beneficiaryInformation,
    refId,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const { bottomsheetRef, onOpen, onClose } = useBottomsheet();
  const { bottomsheetRef: bottomSheetOTPRef, onOpen: onOpenOTP, onClose: onCloseOTP } = useBottomsheet();
  const isFocused = useIsFocused();
  const store = useStore();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  const onNavigate = () => {
    return iframePostMessage('showTransactionHistory', true);
  };

  return (
    <View sx={sxStyles.container}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}></View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('unionPay::payment-instruction.glossary.unionpay')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}></View>
        </View>
      </PageHeaderModalNative>
      <ScrollView sx={sxStyles.scrollViewWrapper} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.topHeader}>
          <Icon name="successSpecial" height={108} width={108} />
          <View sx={sxStyles.textMb}>
            <H6 bold>{t('unionPay::payment-instruction.title--payment-instruction-placed')}</H6>
            <TextCaption>{t('unionPay::payment-instruction.text--two-to-three-business-days')}</TextCaption>
          </View>
        </View>
        <View sx={sxStyles.body}>
          <View sx={{ gap: 8, my: '$5' }}>
            <View sx={sxStyles.sendBox}>
              <TextCaption semiBold>{t('unionPay::payment-instruction.text--you-send')}</TextCaption>
              <View sx={sxStyles.amount}>
                <CurrenciesIcon asset={calcFee?.feeCurrency} hideText={true} />
                <TextBody semiBold>
                  {formatAmountByDecimal({
                    value: (unionType == UnionType.PAYMENT ? calcFee?.attributes?.amount : calcFee?.sourceAmount) || 0,
                    suffix: calcFee?.feeCurrency,
                    decimals: 2
                  })}
                </TextBody>
              </View>
            </View>
            <View sx={sxStyles.sendBox}>
              <TextCaption semiBold>{t('unionPay::payment-instruction.text--recipient-receives')}</TextCaption>
              <View sx={sxStyles.amount}>
                <Icon name="rmb" width={28} height={28} />
                <TextBody semiBold>
                  {formatAmountByDecimal({
                    value: (unionType == UnionType.PAYMENT ? calcFee?.attributes?.destination?.amount : calcFee?.destinationAmount) || 0,
                    suffix: 'RMB',
                    decimals: 2
                  })}
                </TextBody>
              </View>
            </View>
          </View>
          <View sx={{ gap: 12 }}>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.glossary.conversion-rate')}</TextBody>
              <TextBody>
                {formatAmountByDecimal({
                  value: 1 / calcFee?.attributes?.destination?.rate,
                  prefix: '1 RMB =',
                  suffix: calcFee?.feeCurrency,
                  decimals: 4
                })}
              </TextBody>
            </TextBody>
            <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--from-account')}</TextBody>
              <View sx={{ textAlign: 'right' }}>
                <TextBody>
                  {wallet && (wallet?.accountLabel ? t(`earn-invest::glossary.${wallet?.accountLabel}`) : wallet?.accountName || ' - ')}
                </TextBody>
                <TextBody>{wallet && wallet?.accountNumber}</TextBody>
              </View>
            </TextBody>
            <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--to-account')}</TextBody>
              <View sx={{ textAlign: 'right' }}>
                <TextBody>{beneficiaryInformation?.account?.accountLabel}</TextBody>
                <TextBody>{beneficiaryInformation?.account?.accountNumber}</TextBody>
              </View>
            </TextBody>
            {/* <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>Instruction Fee</TextBody>
              <TextBody sx={{ color: '$borderInputError' }}>20.00 HKD</TextBody>
            </TextBody> */}
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.glossary.debit-amount')}</TextBody>
              <TextBody sx={{ color: '$borderInputError' }}>
                {formatAmountByDecimal({
                  value: calcFee?.instructionTotal,
                  suffix: calcFee?.feeCurrency,
                  decimals: 2
                })}
              </TextBody>
            </TextBody>
            <Divider sx={{ my: '$2' }} />
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--purpose')}</TextBody>
              <TextBody>{RemittancePurposeSelectFilters(t).find((f) => f.value === beneficiaryInformation?.purpose)?.label}</TextBody>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--fund-source')}</TextBody>
              <TextBody>{FundSourceSelectFilters(t).find((f) => f.value === beneficiaryInformation?.fundSource)?.label}</TextBody>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--remarks')}</TextBody>
              <TextBody>{beneficiaryInformation?.remark || '-'}</TextBody>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--instruction-time')}</TextBody>
              <TextBody>{instructionTime}</TextBody>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--ref-id')}</TextBody>
              <TextBody>{refId || '-'}</TextBody>
            </TextBody>
          </View>
        </View>
        <View sx={sxStyles.btnView}>
          <UniButton
            sx={{ ...sxStyles.leftBtn, height: 44 }}
            variant={ButtonVariant.outline1}
            label={t('global-bill-payment::glossary.back')}
            onPress={() => {
              onBack && onBack(true);
            }}
          />
          <UniButton
            sx={{ ...sxStyles.rightBtn, height: 44 }}
            labelSx={{ color: '$balck' }}
            label={t('unionPay::payment-instruction.button--check-history')}
            onPress={() => onNavigate()}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
    // padding: '$4'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  button: {
    width: '100%',
    height: '$11',
    mt: '$5'
  },
  scrollViewWrapper: {
    pt: '$4',
    pb: '$7',
    px: '$4'
  },
  viewAlert: {
    mt: '$5'
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '$5'
  },
  textMb: {
    textAlign: 'center',
    gap: 8
  },
  sendBox: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$4',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10
  },
  amount: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '$2'
  },
  itemStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 20
  },
  topCenter: {
    alignItems: 'flex-start'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '$3',
    mt: '$10'
  }
};
